/**
 * @fileoverview Parser para dados históricos financeiros (2020-2024)
 * 
 * @module HistoricoParser
 * @description
 * Extrai e processa dados históricos das DREs de Sétima e 555 Studios
 * dos CSVs extraídos, calculando métricas adicionais (CAGR, médias, etc)
 * 
 * @critical Fundamental para auditabilidade - mostra base das projeções
 */

/**
 * Interface para dados históricos de uma empresa
 */
export interface HistoricoEmpresa {
  empresa: 'Sétima' | '555 Studios';
  anos: {
    [ano: number]: DREHistorico;
  };
  metricas: MetricasHistoricas;
}

/**
 * DRE histórica de um ano específico
 */
export interface DREHistorico {
  ano: number;
  receitaBruta: number;
  impostosVendas: number;
  receitaLiquida: number;
  custosDiretos: number;
  lucroBruto: number;
  despesasOperacionais: number;
  ebitda: number;
  depreciacao: number;
  ebit: number;
  impostosLucro: number;
  lucroLiquido: number;
  
  // Margens (%)
  margens: {
    bruta: number;
    ebitda: number;
    ebit: number;
    liquida: number;
  };
}

/**
 * Métricas calculadas sobre o período histórico
 */
export interface MetricasHistoricas {
  // CAGR (Compound Annual Growth Rate)
  cagr: {
    receita: number;
    ebitda: number;
    lucroLiquido: number;
  };
  
  // Médias do período
  medias: {
    margemBruta: number;
    margemEbitda: number;
    margemLiquida: number;
    receita: number;
  };
  
  // Volatilidade (desvio padrão)
  volatilidade: {
    receita: number;
    margemEbitda: number;
  };
  
  // Período
  periodoInicio: number;
  periodoFim: number;
  anosAnalise: number;
}

/**
 * Dados consolidados de todas as empresas
 */
export interface DadosHistoricosCompletos {
  setima: HistoricoEmpresa;
  studio555: HistoricoEmpresa;
  consolidado: {
    anos: {
      [ano: number]: {
        receitaTotal: number;
        ebitdaTotal: number;
        margemEbitda: number;
      };
    };
    metricas: MetricasHistoricas;
  };
}

/**
 * HistoricoParser - Classe principal de parsing
 */
export class HistoricoParser {
  /**
   * Dados reais extraídos do DRE_Normalizada_Completa.xlsx
   * Fonte: Aba "Sétima Consolidado" e "555 Studios"
   * 
   * @critical Dados 2023 Sétima têm PREJUÍZO de -R$ 535.145,72
   */
  private static readonly DADOS_SETIMA = {
    2020: { receita: 6560028.82, ebitda: 1515712.34 },
    2021: { receita: 8164232.07, ebitda: 2289036.86 },
    2022: { receita: 10238189.48, ebitda: 3288778.71 },
    2023: { 
      receita: 7048065.74, 
      ebitda: 79502.46,
      // Dados reais completos de DRE_Normalizada_Completa.xlsx
      receitaLiquida: 6544261.93,
      custosDiretos: 3814367.53,
      lucroBruto: 2729894.40,
      despesasOperacionais: 2650391.94,
      receitaFinanceira: 0,
      despesasFinanceiras: 8420.84,
      resultadoAntesImpostos: 71081.62,
      impostosLucro: 606227.34,
      lucroLiquido: -535145.72, // PREJUÍZO! (71k - 606k)
    },
    2024: { receita: 8548359.60, ebitda: 954086.99 },
  };

  private static readonly DADOS_555 = {
    2023: { receita: 1132800.00, ebitda: 465742.18 },
    2024: { receita: 3176911.50, ebitda: 1143645.33 },
  };

  /**
   * Premissas de estrutura de DRE (baseadas na análise Excel)
   */
  private static readonly PREMISSAS_SETIMA = {
    impostoVendas: 0.08, // 8%
    margemCustos: 0.35, // Custos diretos ~35% (média)
    despesasOp: 0.25, // Despesas operacionais ~25%
    depreciacao: 0.03, // 3%
    impostoLucro: 0.18, // 18%
  };

  private static readonly PREMISSAS_555 = {
    impostoVendas: 0.06, // 6%
    margemCustos: 0.35, // 35%
    despesasOp: 0.30, // 30%
    depreciacao: 0.02, // 2%
    impostoLucro: 0.18, // 18%
  };

  /**
   * Reconstrói DRE completa a partir de Receita e EBITDA conhecidos
   * 
   * @param dadosReais - Se fornecido, usa dados reais em vez de calcular
   */
  private static reconstruirDRE(
    ano: number,
    receita: number,
    ebitda: number,
    premissas: typeof HistoricoParser.PREMISSAS_SETIMA,
    dadosReais?: Partial<DREHistorico>
  ): DREHistorico {
    // Se temos dados reais completos, usar eles diretamente
    if (dadosReais && dadosReais.lucroLiquido !== undefined) {
      const receitaLiquida = dadosReais.receitaLiquida || receita * (1 - premissas.impostoVendas);
      
      return {
        ano,
        receitaBruta: receita,
        impostosVendas: dadosReais.impostosVendas || receita * premissas.impostoVendas,
        receitaLiquida,
        custosDiretos: dadosReais.custosDiretos || (dadosReais.lucroBruto ? receitaLiquida - dadosReais.lucroBruto : receitaLiquida * premissas.margemCustos),
        lucroBruto: dadosReais.lucroBruto || 0,
        despesasOperacionais: dadosReais.despesasOperacionais || 0,
        ebitda,
        depreciacao: dadosReais.depreciacao || receita * premissas.depreciacao,
        ebit: dadosReais.ebit || ebitda - (dadosReais.depreciacao || receita * premissas.depreciacao),
        impostosLucro: dadosReais.impostosLucro || 0,
        lucroLiquido: dadosReais.lucroLiquido,
        margens: {
          bruta: dadosReais.lucroBruto ? (dadosReais.lucroBruto / receitaLiquida) * 100 : 0,
          ebitda: (ebitda / receitaLiquida) * 100,
          ebit: dadosReais.ebit ? (dadosReais.ebit / receitaLiquida) * 100 : 0,
          liquida: (dadosReais.lucroLiquido / receitaLiquida) * 100,
        },
      };
    }
    
    // Caso contrário, reconstruir a partir de premissas
    const receitaBruta = receita;
    const impostosVendas = receitaBruta * premissas.impostoVendas;
    const receitaLiquida = receitaBruta - impostosVendas;
    
    // Trabalhar backward do EBITDA
    const depreciacao = receitaBruta * premissas.depreciacao;
    const ebit = ebitda - depreciacao;
    const impostosLucro = ebit * premissas.impostoLucro;
    const lucroLiquido = ebit - impostosLucro;
    
    // Trabalhar forward para completar
    const custosDiretos = receitaLiquida * premissas.margemCustos;
    const lucroBruto = receitaLiquida - custosDiretos;
    const despesasOperacionais = lucroBruto - ebitda;
    
    // Calcular margens
    const margemBruta = (lucroBruto / receitaLiquida) * 100;
    const margemEbitda = (ebitda / receitaLiquida) * 100;
    const margemEbit = (ebit / receitaLiquida) * 100;
    const margemLiquida = (lucroLiquido / receitaLiquida) * 100;

    return {
      ano,
      receitaBruta,
      impostosVendas,
      receitaLiquida,
      custosDiretos,
      lucroBruto,
      despesasOperacionais,
      ebitda,
      depreciacao,
      ebit,
      impostosLucro,
      lucroLiquido,
      margens: {
        bruta: margemBruta,
        ebitda: margemEbitda,
        ebit: margemEbit,
        liquida: margemLiquida,
      },
    };
  }

  /**
   * Calcula CAGR (Compound Annual Growth Rate)
   * Formula: ((Valor Final / Valor Inicial)^(1/anos)) - 1
   */
  private static calcularCAGR(valorInicial: number, valorFinal: number, anos: number): number {
    if (valorInicial <= 0 || anos <= 0) return 0;
    return Math.pow(valorFinal / valorInicial, 1 / anos) - 1;
  }

  /**
   * Calcula média de um array de valores
   */
  private static calcularMedia(valores: number[]): number {
    if (valores.length === 0) return 0;
    return valores.reduce((sum, val) => sum + val, 0) / valores.length;
  }

  /**
   * Calcula desvio padrão (volatilidade)
   */
  private static calcularDesvioPadrao(valores: number[]): number {
    if (valores.length === 0) return 0;
    const media = this.calcularMedia(valores);
    const variancia = valores.reduce((sum, val) => sum + Math.pow(val - media, 2), 0) / valores.length;
    return Math.sqrt(variancia);
  }

  /**
   * Calcula métricas históricas de uma empresa
   */
  private static calcularMetricas(
    anos: { [ano: number]: DREHistorico }
  ): MetricasHistoricas {
    const anosOrdenados = Object.keys(anos).map(Number).sort();
    const anoInicial = anosOrdenados[0];
    const anoFinal = anosOrdenados[anosOrdenados.length - 1];
    const numAnos = anoFinal - anoInicial;

    const receitas = anosOrdenados.map(ano => anos[ano].receitaBruta);
    const ebitdas = anosOrdenados.map(ano => anos[ano].ebitda);
    const lucros = anosOrdenados.map(ano => anos[ano].lucroLiquido);
    const margensEbitda = anosOrdenados.map(ano => anos[ano].margens.ebitda);
    const margensBruta = anosOrdenados.map(ano => anos[ano].margens.bruta);
    const margensLiquida = anosOrdenados.map(ano => anos[ano].margens.liquida);

    return {
      cagr: {
        receita: this.calcularCAGR(receitas[0], receitas[receitas.length - 1], numAnos),
        ebitda: this.calcularCAGR(ebitdas[0], ebitdas[ebitdas.length - 1], numAnos),
        lucroLiquido: this.calcularCAGR(lucros[0], lucros[lucros.length - 1], numAnos),
      },
      medias: {
        margemBruta: this.calcularMedia(margensBruta),
        margemEbitda: this.calcularMedia(margensEbitda),
        margemLiquida: this.calcularMedia(margensLiquida),
        receita: this.calcularMedia(receitas),
      },
      volatilidade: {
        receita: this.calcularDesvioPadrao(receitas),
        margemEbitda: this.calcularDesvioPadrao(margensEbitda),
      },
      periodoInicio: anoInicial,
      periodoFim: anoFinal,
      anosAnalise: anosOrdenados.length,
    };
  }

  /**
   * Extrai dados históricos da Sétima
   */
  private static extrairDadosSetima(): HistoricoEmpresa {
    const anos: { [ano: number]: DREHistorico } = {};

    Object.entries(this.DADOS_SETIMA).forEach(([anoStr, dados]) => {
      const ano = parseInt(anoStr);
      
      // Para 2023, usar dados reais do Excel (com prejuízo)
      const dadosReais = ano === 2023 && 'lucroLiquido' in dados 
        ? {
            receitaLiquida: dados.receitaLiquida,
            custosDiretos: dados.custosDiretos,
            lucroBruto: dados.lucroBruto,
            despesasOperacionais: dados.despesasOperacionais,
            impostosLucro: dados.impostosLucro,
            lucroLiquido: dados.lucroLiquido, // -535.145,72 (PREJUÍZO)
          }
        : undefined;
      
      anos[ano] = this.reconstruirDRE(
        ano,
        dados.receita,
        dados.ebitda,
        this.PREMISSAS_SETIMA,
        dadosReais
      );
    });

    return {
      empresa: 'Sétima',
      anos,
      metricas: this.calcularMetricas(anos),
    };
  }

  /**
   * Extrai dados históricos da 555 Studios
   */
  private static extrairDados555(): HistoricoEmpresa {
    const anos: { [ano: number]: DREHistorico } = {};

    Object.entries(this.DADOS_555).forEach(([anoStr, dados]) => {
      const ano = parseInt(anoStr);
      anos[ano] = this.reconstruirDRE(
        ano,
        dados.receita,
        dados.ebitda,
        this.PREMISSAS_555
      );
    });

    return {
      empresa: '555 Studios',
      anos,
      metricas: this.calcularMetricas(anos),
    };
  }

  /**
   * Calcula dados consolidados (Sétima + 555)
   */
  private static calcularConsolidado(
    setima: HistoricoEmpresa,
    studio555: HistoricoEmpresa
  ) {
    const anos: { [ano: number]: { receitaTotal: number; ebitdaTotal: number; margemEbitda: number } } = {};

    // Combinar todos os anos de ambas empresas
    const todosAnos = new Set([
      ...Object.keys(setima.anos).map(Number),
      ...Object.keys(studio555.anos).map(Number),
    ]);

    todosAnos.forEach(ano => {
      const receitaSetima = setima.anos[ano]?.receitaBruta || 0;
      const ebitdaSetima = setima.anos[ano]?.ebitda || 0;
      const receita555 = studio555.anos[ano]?.receitaBruta || 0;
      const ebitda555 = studio555.anos[ano]?.ebitda || 0;

      const receitaTotal = receitaSetima + receita555;
      const ebitdaTotal = ebitdaSetima + ebitda555;
      const margemEbitda = receitaTotal > 0 ? (ebitdaTotal / receitaTotal) * 100 : 0;

      anos[ano] = {
        receitaTotal,
        ebitdaTotal,
        margemEbitda,
      };
    });

    // Calcular métricas consolidadas (apenas anos com dados completos)
    const anosCompletos = Array.from(todosAnos).sort();
    const receitaInicial = anos[anosCompletos[0]].receitaTotal;
    const receitaFinal = anos[anosCompletos[anosCompletos.length - 1]].receitaTotal;
    const ebitdaInicial = anos[anosCompletos[0]].ebitdaTotal;
    const ebitdaFinal = anos[anosCompletos[anosCompletos.length - 1]].ebitdaTotal;

    const metricas: MetricasHistoricas = {
      cagr: {
        receita: this.calcularCAGR(receitaInicial, receitaFinal, anosCompletos.length - 1),
        ebitda: this.calcularCAGR(ebitdaInicial, ebitdaFinal, anosCompletos.length - 1),
        lucroLiquido: 0, // Não calculamos lucro líquido consolidado aqui
      },
      medias: {
        margemBruta: 0,
        margemEbitda: this.calcularMedia(anosCompletos.map(ano => anos[ano].margemEbitda)),
        margemLiquida: 0,
        receita: this.calcularMedia(anosCompletos.map(ano => anos[ano].receitaTotal)),
      },
      volatilidade: {
        receita: this.calcularDesvioPadrao(anosCompletos.map(ano => anos[ano].receitaTotal)),
        margemEbitda: this.calcularDesvioPadrao(anosCompletos.map(ano => anos[ano].margemEbitda)),
      },
      periodoInicio: anosCompletos[0],
      periodoFim: anosCompletos[anosCompletos.length - 1],
      anosAnalise: anosCompletos.length,
    };

    return { anos, metricas };
  }

  /**
   * Método principal: extrai todos os dados históricos
   */
  public static extrairDadosCompletos(): DadosHistoricosCompletos {
    const setima = this.extrairDadosSetima();
    const studio555 = this.extrairDados555();
    const consolidado = this.calcularConsolidado(setima, studio555);

    return {
      setima,
      studio555,
      consolidado,
    };
  }
}

/**
 * Hook de conveniência para usar dados históricos
 */
export const useDadosHistoricos = (): DadosHistoricosCompletos => {
  return HistoricoParser.extrairDadosCompletos();
};
